/**
 * Chrome of the guided listing setup: top bar, the step rail (laptop) or compact progress header
 * (below the laptop breakpoint), the sticky bottom action bar, the unsaved-changes badge and leave
 * prompt, and the quiet "Discard this draft" action. Steps render their own heading and body.
 *
 * Design decision: the guide stays inside the app shell (unlike the preview, which is a rendering of
 * the prospect page). The shell carries the Help button every authenticated screen must have (BR-15),
 * the billing banner and the navigation, and a landlord mid-setup still needs all three. The cost is a
 * narrower content column at laptop width than the standalone mocks show.
 */

import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronLeft } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../../components/ui/sheet';
import { StepRail, type StepRailItem } from '../../../components/layout';
import { ListingPaidPeriodIndicator } from '../../../components/leasing/ListingPaidPeriodIndicator';
import type { Listing } from '../../../lib/leasing.api';
import { LISTING_STATUS_BADGE_VARIANTS, LISTING_STATUS_LABELS } from '../../../components/leasing/listing-status';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { LifecycleConfirmDialog } from '../listing-detail/shared';
import { GUIDE_STEPS, type GuideStepKey } from './guide-steps';
import type { GuideState } from './useGuideState';

interface GuideShellProps {
  stepKey: GuideStepKey;
  /** "Maple Court · Unit 2B", or null while the unit loads. */
  unitLabel: string | null;
  guide: GuideState;
  /** The draft Listing once it exists: its status and payment badges show beside the title (US-55, BR-110). */
  listing: Listing | null;
  /** Where each rail entry goes (the route differs before and after the draft Listing exists). */
  stepPath: (key: GuideStepKey) => string;
  dirty: boolean;
  saving: boolean;
  /** Saves the step on screen; resolves true when saved (or nothing was edited). */
  save: () => Promise<boolean>;
  /** "Save and continue": save, then advance. */
  onContinue: () => void;
  /** Previous step's path; absent on the first step. A link, so the leave prompt covers it. */
  backTo?: string;
  primaryLabel: string;
  primaryTestId: string;
  /** Present once a draft Listing exists and the person may close it. */
  onDiscardDraft?: () => Promise<void>;
  /**
   * The step body, given the action bar element so a step can seat it inside its own card (Review and
   * publish does); every other step renders it after its content.
   */
  children: (actionBar: ReactNode) => ReactNode;
}

export function GuideShell({
  stepKey,
  unitLabel,
  guide,
  listing,
  stepPath,
  dirty,
  saving,
  save,
  onContinue,
  backTo,
  primaryLabel,
  primaryTestId,
  onDiscardDraft,
  children,
}: GuideShellProps) {
  const navigate = useNavigate();
  const [allStepsOpen, setAllStepsOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [discardError, setDiscardError] = useState<string | null>(null);

  const { dialog: leaveDialog } = useUnsavedChangesGuard({
    when: dirty,
    onSave: save,
    message: 'Save this step before you leave? If you discard your edits, you keep what you last saved.',
  });

  const index = GUIDE_STEPS.findIndex((step) => step.key === stepKey);
  const stepLabel = GUIDE_STEPS[index]!.label;
  const railItems: StepRailItem[] = GUIDE_STEPS.map((step) => ({
    key: step.key,
    label: step.label,
    status: guide.steps[step.key].status,
    summary: guide.steps[step.key].summary,
    loading: guide.steps[step.key].loading,
    to: stepPath(step.key),
  }));

  const handleSaveAndExit = async () => {
    if (dirty && !(await save())) return;
    navigate('/leasing/listings');
  };

  const handleDiscard = async () => {
    if (!onDiscardDraft) return;
    setDiscarding(true);
    setDiscardError(null);
    try {
      await onDiscardDraft();
      setDiscardOpen(false);
    } catch (err) {
      setDiscardError(err instanceof Error ? err.message : 'Failed to discard this draft');
      setDiscardOpen(false);
    } finally {
      setDiscarding(false);
    }
  };

  // Fixed above the tab bar below the laptop breakpoint (the shell's <main> is not the scroller, so sticky
  // never sticks); in flow on laptop.
  const actionBar = (
    <div
      data-testid="guide-action-bar"
      className="fixed inset-x-0 bottom-[calc(var(--bottom-tab-height)+env(safe-area-inset-bottom,0px))] z-30 border-t border-border bg-card px-[var(--page-padding-x)] py-3 md:bottom-0 md:left-[var(--sidebar-width)] xl:static xl:z-auto xl:mt-6 xl:border-t-0 xl:bg-transparent xl:p-0"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div
          className="flex min-h-[24px] items-center justify-center gap-2 text-xs text-muted-foreground sm:order-2 sm:flex-1 sm:justify-start"
          aria-live="polite"
        >
          {dirty && (
            <>
              <Badge variant="warning" data-testid="guide-unsaved-badge">Unsaved changes</Badge>
              <span>Saved when you continue.</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 sm:contents">
          {backTo && (
            <Button asChild variant="ghost" className="sm:order-1">
              <Link to={backTo} data-testid="guide-back">
                <ArrowLeft aria-hidden="true" />
                Back
              </Link>
            </Button>
          )}
          <Button
            type="button"
            className="flex-1 sm:order-3 sm:flex-none"
            data-testid={primaryTestId}
            onClick={onContinue}
            disabled={saving}
          >
            {saving ? 'Saving…' : primaryLabel}
            {!saving && <ArrowRight aria-hidden="true" />}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="listing-guide" className="mx-auto w-full max-w-[1280px] pb-6">
      {/* Top bar */}
      <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border px-[var(--page-padding-x)] py-2">
        <div className="flex min-w-0 items-center gap-2 text-sm">
          <Link
            to="/leasing/listings"
            data-testid="guide-back-to-listings"
            className="inline-flex min-h-[44px] items-center gap-1 rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            <span className="max-sm:sr-only">Listings</span>
          </Link>
          <span className="flex min-w-0 flex-col sm:flex-row sm:items-baseline sm:gap-2" data-testid="guide-title">
            <span className="font-semibold text-foreground">
              <span aria-hidden="true" className="max-sm:hidden mr-2 font-normal text-muted-foreground">/</span>
              New listing
            </span>
            {unitLabel && (
              <span className="break-words text-muted-foreground" data-testid="guide-unit-label">
                {unitLabel}
              </span>
            )}
          </span>
        </div>
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
          {listing && (
            <>
              {/* Publication status and payment state side by side, so "Paid · Draft" is unmistakable (US-55, BR-110). */}
              <Badge data-testid="listing-status-badge" variant={LISTING_STATUS_BADGE_VARIANTS[listing.status]}>
                {LISTING_STATUS_LABELS[listing.status]}
              </Badge>
              <ListingPaidPeriodIndicator listing={listing} variant="header" />
            </>
          )}
          <Button
            type="button"
            variant="outline"
            data-testid="guide-save-exit"
            onClick={() => void handleSaveAndExit()}
            disabled={saving}
          >
            Save and exit
          </Button>
        </div>
      </header>

      <div className="flex flex-col xl:flex-row xl:gap-10">
        {/* Rail (laptop) */}
        <aside className="hidden shrink-0 px-[var(--page-padding-x)] pt-8 xl:block xl:w-[272px] xl:pr-0" data-testid="guide-rail">
          <h2 className="text-lg font-semibold text-foreground">Get this unit listed</h2>
          <p className="mb-5 mt-1 text-sm text-muted-foreground">
            Seven steps. Each is saved when you continue, so you can leave and come back.
          </p>
          <StepRail steps={railItems} currentKey={stepKey} ariaLabel="Listing setup steps" data-testid="guide-steps" />
          {onDiscardDraft && (
            <Button
              type="button"
              variant="ghost"
              data-testid="guide-discard-draft"
              className="mt-6 text-muted-foreground hover:text-destructive"
              onClick={() => setDiscardOpen(true)}
              disabled={discarding}
            >
              Discard this draft
            </Button>
          )}
        </aside>

        {/* Compact progress header (below laptop) */}
        <div className="px-[var(--page-padding-x)] pt-4 xl:hidden" data-testid="guide-progress">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{`Step ${index + 1} of ${GUIDE_STEPS.length}`}</span>
              {` · ${stepLabel}`}
            </p>
            <Button
              type="button"
              variant="link"
              data-testid="guide-all-steps"
              onClick={() => setAllStepsOpen(true)}
            >
              All steps
            </Button>
          </div>
          <div
            role="progressbar"
            aria-label="Setup progress"
            aria-valuemin={1}
            aria-valuemax={GUIDE_STEPS.length}
            aria-valuenow={index + 1}
            className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"
          >
            <div className="h-full rounded-full bg-primary" style={{ width: `${((index + 1) / GUIDE_STEPS.length) * 100}%` }} />
          </div>
        </div>

        {/* Content column */}
        <div className="min-w-0 flex-1 pb-28 xl:pb-0 xl:pt-8">
          <div className="px-[var(--page-padding-x)] pt-4 xl:pt-0 xl:pl-0 xl:pr-[var(--page-padding-x)]">
            <p className="mb-1 hidden text-xs text-muted-foreground xl:block" data-testid="guide-step-count">
              {`Step ${index + 1} of ${GUIDE_STEPS.length}`}
            </p>
            {discardError && (
              <div role="alert" data-testid="guide-discard-error" className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                {discardError}
              </div>
            )}
            {children(actionBar)}
          </div>

          {/* Discard (below laptop: the rail is hidden) */}
          {onDiscardDraft && (
            <div className="px-[var(--page-padding-x)] pt-2 xl:hidden">
              <Button
                type="button"
                variant="ghost"
                data-testid="guide-discard-draft-compact"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => setDiscardOpen(true)}
                disabled={discarding}
              >
                Discard this draft
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* All steps (below laptop) */}
      <Sheet open={allStepsOpen} onOpenChange={setAllStepsOpen}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto" data-testid="guide-all-steps-sheet">
          <SheetHeader className="text-left">
            <SheetTitle>All steps</SheetTitle>
            <SheetDescription>Open any step. Each is saved when you continue.</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            <StepRail
              steps={railItems}
              currentKey={stepKey}
              ariaLabel="All listing setup steps"
              onNavigate={() => setAllStepsOpen(false)}
              data-testid="guide-all-steps-list"
            />
          </div>
        </SheetContent>
      </Sheet>

      <LifecycleConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Discard this draft"
        description="This closes the draft listing. Your marketing description and photos stay on the unit, and you can start a new listing for it any time."
        confirmLabel="Discard draft"
        pendingLabel="Discarding…"
        onConfirm={() => void handleDiscard()}
        isSubmitting={discarding}
        destructive
        testIdPrefix="guide-discard"
      />
      {leaveDialog}
    </div>
  );
}
